/**
 * LiveFilterToggle
 * Clean pill switch: "Live" thumb slides right and turns red when on.
 */

import React, { useMemo, useRef, useEffect } from 'react';
import { View, Text, Pressable, Animated, Easing } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import {
  getLiveFilterToggleStyles,
  LIVE_TOGGLE_THUMB_TRAVEL,
} from './LiveFilterToggle.styles';

const DURATION = 220;

export interface LiveFilterToggleProps {
  value: boolean;
  onValueChange: (on: boolean) => void;
  testID?: string;
}

export const LiveFilterToggle: React.FC<LiveFilterToggleProps> = ({
  value,
  onValueChange,
  testID = 'live-filter-toggle',
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getLiveFilterToggleStyles(theme), [theme]);
  const anim = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(anim, {
      toValue: value ? 1 : 0,
      duration: DURATION,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [value, anim]);

  const thumbTranslateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, LIVE_TOGGLE_THUMB_TRAVEL],
  });
  const thumbRedOpacity = anim;

  return (
    <Pressable
      onPress={() => onValueChange(!value)}
      style={({ pressed }) => [styles.container, pressed && styles.containerPressed]}
      hitSlop={10}
      testID={testID}
    >
      <View style={styles.track} />
      <Animated.View
        style={[styles.thumb, { transform: [{ translateX: thumbTranslateX }] }]}
      >
        <Animated.View
          style={[styles.thumbActiveOverlay, { opacity: thumbRedOpacity }]}
        />
        <Text style={styles.thumbLabel} numberOfLines={1}>
          Live
        </Text>
      </Animated.View>
    </Pressable>
  );
};
